import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, Waves, Thermometer, Wind, Droplets, TrendingUp,
  TrendingDown, Minus, Ship, Activity, Eye, Loader2, MapPin,
  ExternalLink, RefreshCw, ChevronDown, Search
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let lastLakeNearbyCache = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchNearbyWithRetry(lat, lon) {
  const attempts = [12000, 18000, 24000];
  let lastError;
  for (let index = 0; index < attempts.length; index += 1) {
    try {
      const response = await axios.get(`${API}/weather/lake/nearby`, {
        params: { lat, lon },
        timeout: attempts[index],
      });
      lastLakeNearbyCache = response.data;
      return response;
    } catch (error) {
      lastError = error;
      if (index < attempts.length - 1) {
        console.warn(`Lake nearby fetch retry ${index + 1}/${attempts.length - 1}:`, error.message);
        await wait(600 * (index + 1));
      }
    }
  }
  if (lastLakeNearbyCache) {
    return { data: lastLakeNearbyCache, stale: true };
  }
  throw lastError;
}

function MiniChart({ data, dataKey, color, label, unit, height = 120 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d[dataKey]).filter(v => v != null);
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 100, h = height, padding = 4;
  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (w - padding * 2);
    const y = h - padding - ((v - min) / range) * (h - padding * 2);
    return `${x},${y}`;
  }).join(" ");
  const areaPoints = `${padding},${h - padding} ${points} ${w - padding},${h - padding}`;
  const latest = values[values.length - 1];
  const first = values[0];
  const change = latest - first;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium opacity-60">{label}</span>
        <div className="flex items-center gap-1 text-xs">
          <span className="font-bold">{latest}{unit}</span>
          <span className={`flex items-center gap-0.5 ${change > 0 ? 'text-red-400' : change < 0 ? 'text-blue-400' : 'text-gray-400'}`}>
            {change > 0 ? <TrendingUp className="w-3 h-3" /> : change < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            {change > 0 ? '+' : ''}{change.toFixed(1)}{unit}
          </span>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: `${height}px` }}>
        <polygon points={areaPoints} fill={color} opacity="0.1" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={parseFloat(points.split(" ").pop().split(",")[0])} cy={parseFloat(points.split(" ").pop().split(",")[1])} r="3" fill={color} />
      </svg>
      <div className="flex justify-between text-xs opacity-30 mt-1">
        <span>7 days ago</span><span>Now</span>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, isDarkMode, trend }) {
  const testId = `lake-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}
      data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-medium opacity-60">{label}</span>
        {trend && <span className="ml-auto">
          {trend === "rising" && <TrendingUp className="w-3 h-3 text-red-400" />}
          {trend === "falling" && <TrendingDown className="w-3 h-3 text-blue-400" />}
          {trend === "stable" && <Minus className="w-3 h-3 text-gray-400" />}
        </span>}
      </div>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>{sub}</p>}
    </motion.div>
  );
}

function LakeSourceOverlay({ detail, selectedLake, isDarkMode }) {
  if (!detail && !selectedLake) return null;
  const stationSource = detail?.site?.agency || "USGS";
  const lakeSource = selectedLake?.source || "OSM/USGS";
  return (
    <div className={`mb-6 rounded-xl px-4 py-3 border ${isDarkMode ? 'bg-blue-500/10 border-blue-400/20 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'}`} data-testid="lake-source-comparison-badge">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="font-semibold">Lake source overlay</span>
        <span className="text-xs opacity-70">{lakeSource} discovery + {stationSource} station data + live weather context</span>
      </div>
    </div>
  );
}

export default function LakePage() {
  const { user, token, updatePreferences } = useAuth();
  const [searchParams] = useSearchParams();
  const lat = parseFloat(searchParams.get("lat")) || user?.preferences?.default_location?.lat || 35.2098;
  const lon = parseFloat(searchParams.get("lon")) || user?.preferences?.default_location?.lon || -86.0794;
  const isDarkMode = user?.preferences?.theme === "dark";

  const [lakes, setLakes] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedLake, setSelectedLake] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [isPremium, setIsPremium] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          timeout: 10000,
        });
        setIsPremium(res.data?.is_premium || res.data?.is_trial || user?.role === "admin");
      } catch (err) {
        console.error("Subscription check error:", err.message);
        setIsPremium(user?.role === "admin" || user?.subscription?.plan === "premium" || user?.subscription?.status === "active");
      }
    };
    if (user) check();
    else { const t = setTimeout(() => setIsPremium(false), 2000); return () => clearTimeout(t); }
  }, [user, token]);

  const autoSelectBest = useCallback((allLakes, allStations) => {
    // Prefer a lake that has a USGS station linked
    const lakeWithData = allLakes.find(l => l.usgs_station);
    if (lakeWithData) {
      setSelectedLake(lakeWithData);
      setSelectedStation(lakeWithData.usgs_station);
    } else if (allLakes.length > 0) {
      setSelectedLake(allLakes[0]);
      if (allStations.length > 0) setSelectedStation(allStations[0].site_code);
    } else if (allStations.length > 0) {
      setSelectedStation(allStations[0].site_code);
    }
  }, []);

  const fetchNearby = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchNearbyWithRetry(lat, lon);
      const allLakes = res.data.lakes || [];
      const allStations = res.data.stations || [];
      setLakes(allLakes);
      setStations(allStations);

      // Auto-select: user's preferred lake > closest lake with USGS data > closest lake > closest station
      const savedPref = user?.preferences?.preferred_lake;
      
      if (savedPref) {
        // Check if it's a lake name or station code
        const foundLake = allLakes.find(l => l.name === savedPref);
        const foundStation = allStations.find(s => s.site_code === savedPref);
        if (foundLake) {
          setSelectedLake(foundLake);
          if (foundLake.usgs_station) setSelectedStation(foundLake.usgs_station);
          else if (allStations.length > 0) setSelectedStation(allStations[0].site_code);
        } else if (foundStation) {
          setSelectedStation(foundStation.site_code);
        } else {
          autoSelectBest(allLakes, allStations);
        }
      } else {
        autoSelectBest(allLakes, allStations);
      }
    } catch (err) {
      console.error("Nearby error:", err);
      setLakes((existing) => existing.length ? existing : []);
      setStations((existing) => existing.length ? existing : []);
    } finally {
      setLoading(false);
    }
  }, [autoSelectBest, lat, lon, user]);

  useEffect(() => { fetchNearby(); }, [fetchNearby]);

  const fetchDetail = useCallback(async (siteCode) => {
    if (!siteCode) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/weather/lake/${siteCode}`, { params: { lat, lon }, timeout: 15000 });
      setDetail(res.data);
    } catch (err) { console.error("Lake detail error:", err); }
    finally { setDetailLoading(false); }
  }, [lat, lon]);

  useEffect(() => { if (selectedStation) fetchDetail(selectedStation); }, [selectedStation, fetchDetail]);

  const handleSelectLake = useCallback(async (lake) => {
    setSelectedLake(lake);
    setShowPicker(false);
    setSearchFilter("");
    if (lake.usgs_station) {
      setSelectedStation(lake.usgs_station);
    }
    // Save preference
    try {
      await axios.put(`${API}/user/preferences`, { preferred_lake: lake.name });
      if (updatePreferences) updatePreferences({ preferred_lake: lake.name });
    } catch (e) { console.error("Save lake pref:", e); }
  }, [updatePreferences]);

  const handleSelectStation = useCallback(async (station) => {
    setSelectedLake(null);
    setSelectedStation(station.site_code);
    setShowPicker(false);
    setSearchFilter("");
    try {
      await axios.put(`${API}/user/preferences`, { preferred_lake: station.site_code });
      if (updatePreferences) updatePreferences({ preferred_lake: station.site_code });
    } catch (e) { console.error("Save station pref:", e); }
  }, [updatePreferences]);

  const filteredLakes = lakes.filter(l => l.name.toLowerCase().includes(searchFilter.toLowerCase()));
  const filteredStations = stations.filter(s => s.name.toLowerCase().includes(searchFilter.toLowerCase()));

  if (isPremium === null || loading) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center gap-3 ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Loading lakes & water data...</p>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center gap-4 ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <Waves className="w-16 h-16 text-blue-400 opacity-40" />
        <h2 className="text-xl font-bold">Premium Feature</h2>
        <p className={`${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Upgrade to access Lake & Water monitoring.</p>
        <Link to="/subscription" className="px-6 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-400 transition-colors">Upgrade to Premium</Link>
      </div>
    );
  }

  const displayName = selectedLake?.name || detail?.site?.name || "Select a Lake";

  return (
    <div data-testid="lake-page" className={`min-h-screen pb-12 ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="lake-back-btn" className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <button data-testid="lake-refresh-button" onClick={() => { fetchNearby(); if (selectedStation) fetchDetail(selectedStation); }}
            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'} transition-colors`}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Title + Lake Picker */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <Waves className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Lake & Water Monitor</h1>
                <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  {lakes.length} lakes & {stations.length} monitoring stations nearby
                </p>
              </div>
            </div>

            {/* Lake Picker Dropdown */}
            <div className="relative">
              <button data-testid="lake-picker-btn" onClick={() => setShowPicker(!showPicker)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium min-w-[220px] ${isDarkMode ? 'bg-white/10 hover:bg-white/15' : 'bg-white border border-gray-200 hover:bg-gray-50'} transition-colors`}>
                <MapPin className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="max-w-[200px] truncate text-left flex-1">{displayName}</span>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showPicker && (
                  <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                    className={`absolute right-0 top-14 z-50 w-96 max-h-[28rem] rounded-xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-[#1a1f2e] border border-white/10' : 'bg-white border border-gray-200'}`}>
                    
                    {/* Search */}
                    <div className={`p-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                      <div className="relative">
                        <Search className={`absolute left-3 top-2.5 w-4 h-4 ${isDarkMode ? 'text-white/30' : 'text-gray-400'}`} />
                        <input
                          data-testid="lake-search-input"
                          type="text"
                          placeholder="Search lakes or stations..."
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-white/5 text-white placeholder:text-white/30 border border-white/10' : 'bg-gray-50 text-gray-900 placeholder:text-gray-400 border border-gray-200'}`}
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="overflow-y-auto max-h-[22rem]">
                      {/* Lakes Section */}
                      {filteredLakes.length > 0 && (
                        <div>
                          <p className={`px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-blue-400/60' : 'text-blue-600/60'}`}>
                            Lakes & Reservoirs ({filteredLakes.length})
                          </p>
                          {filteredLakes.map(lake => (
                            <button key={lake.name} data-testid={`lake-option-${lake.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} onClick={() => handleSelectLake(lake)}
                              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-2 transition-colors ${
                                selectedLake?.name === lake.name ? (isDarkMode ? 'bg-blue-500/20' : 'bg-blue-50') : 'hover:bg-blue-500/10'
                              }`}>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{lake.name}</p>
                                <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                                  {lake.distance_mi} mi · {lake.type === 'reservoir' ? 'Reservoir' : 'Lake'}
                                  {lake.usgs_station && <span className="ml-1 text-green-400">· USGS data</span>}
                                </p>
                              </div>
                              <div className="flex gap-2 text-xs shrink-0">
                                {lake.water_temp_f && <span className="text-cyan-400">{lake.water_temp_f}°F</span>}
                                {lake.gage_height_ft && <span className="text-blue-400">{lake.gage_height_ft}ft</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* USGS Stations Section */}
                      {filteredStations.length > 0 && (
                        <div>
                          <p className={`px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-green-400/60' : 'text-green-600/60'}`}>
                            USGS Monitoring Stations ({filteredStations.length})
                          </p>
                          {filteredStations.map(st => (
                            <button key={st.site_code} data-testid={`usgs-station-option-${st.site_code}`} onClick={() => handleSelectStation(st)}
                              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-2 transition-colors ${
                                selectedStation === st.site_code && !selectedLake ? (isDarkMode ? 'bg-green-500/20' : 'bg-green-50') : 'hover:bg-green-500/10'
                              }`}>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{st.name}</p>
                                <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                                  {st.distance_mi} mi · {st.site_code}
                                </p>
                              </div>
                              <div className="flex gap-2 text-xs shrink-0">
                                {st.water_temp_f && <span className="text-cyan-400">{st.water_temp_f}°F</span>}
                                {st.gage_height_ft && <span className="text-blue-400">{st.gage_height_ft}ft</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {filteredLakes.length === 0 && filteredStations.length === 0 && (
                        <p className={`text-center py-8 text-sm ${isDarkMode ? 'text-white/30' : 'text-gray-400'}`}>No results found</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Selected lake info banner */}
          {selectedLake && (
            <div className={`mt-4 flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
              <Waves className="w-5 h-5 text-blue-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{selectedLake.name}</p>
                <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-500'}`}>
                  {selectedLake.distance_mi} mi away · {selectedLake.type === 'reservoir' ? 'Reservoir' : 'Lake'}
                  {selectedLake.usgs_station && ` · Monitored by USGS station ${selectedLake.usgs_station_name}`}
                  {!selectedLake.usgs_station && ' · No USGS monitoring station linked'}
                </p>
              </div>
            </div>
          )}
        </div>

        <LakeSourceOverlay detail={detail} selectedLake={selectedLake} isDarkMode={isDarkMode} />

        {/* Loading detail */}
        {detailLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        )}

        {/* Detail View */}
        {detail && !detailLoading && (
          <>
            <section className="mb-8">
              <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
                Current Conditions {selectedLake ? `— ${selectedLake.name}` : `— ${detail.site?.name}`}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {detail.current?.water_temp_f != null && (
                  <StatCard icon={Thermometer} label="Water Temp" value={`${detail.current.water_temp_f}°F`}
                    sub={detail.trends?.water_temp ? `${detail.trends.water_temp.direction} ${detail.trends.water_temp.change_f > 0 ? '+' : ''}${detail.trends.water_temp.change_f}°F this week` : ""}
                    color="text-cyan-400" isDarkMode={isDarkMode} trend={detail.trends?.water_temp?.direction} />
                )}
                {detail.current?.gage_height_ft != null && (
                  <StatCard icon={Waves} label="Water Level" value={`${detail.current.gage_height_ft} ft`}
                    sub={detail.trends?.gage_height ? `${detail.trends.gage_height.direction} ${detail.trends.gage_height.change_ft > 0 ? '+' : ''}${detail.trends.gage_height.change_ft} ft this week` : ""}
                    color="text-blue-400" isDarkMode={isDarkMode} trend={detail.trends?.gage_height?.direction} />
                )}
                {detail.current?.discharge_cfs != null && (
                  <StatCard icon={Droplets} label="Flow Rate" value={`${detail.current.discharge_cfs} cfs`}
                    sub="Cubic feet/second" color="text-indigo-400" isDarkMode={isDarkMode} />
                )}
                {detail.current?.dissolved_oxygen_mg_l != null && (
                  <StatCard icon={Activity} label="Dissolved Oxygen" value={`${detail.current.dissolved_oxygen_mg_l} mg/L`}
                    sub={detail.river_conditions?.quality_notes?.[0]} color="text-emerald-400" isDarkMode={isDarkMode} />
                )}
                {detail.current?.ph != null && (
                  <StatCard icon={Activity} label="pH" value={detail.current.ph}
                    sub="Water chemistry" color="text-lime-400" isDarkMode={isDarkMode} />
                )}
                {detail.current?.turbidity_fnu != null && (
                  <StatCard icon={Eye} label="Turbidity" value={`${detail.current.turbidity_fnu} FNU`}
                    sub="Water clarity" color="text-cyan-400" isDarkMode={isDarkMode} />
                )}
                {detail.weather?.air_temp_f != null && (
                  <StatCard icon={Thermometer} label="Air Temp" value={`${detail.weather.air_temp_f}°F`}
                    sub={`Humidity ${detail.weather.humidity}%`} color="text-amber-400" isDarkMode={isDarkMode} />
                )}
                {detail.weather?.wind_speed_mph != null && (
                  <StatCard icon={Wind} label="Wind" value={`${detail.weather.wind_speed_mph} mph`}
                    sub={detail.weather.wind_gust_mph ? `Gusts ${detail.weather.wind_gust_mph} mph` : detail.weather.description}
                    color="text-slate-400" isDarkMode={isDarkMode} />
                )}
                {detail.weather?.visibility_mi != null && (
                  <StatCard icon={Eye} label="Visibility" value={`${detail.weather.visibility_mi} mi`}
                    sub={detail.conditions?.lake_surface} color="text-green-400" isDarkMode={isDarkMode} />
                )}
                <StatCard icon={Ship} label="Boating" value={`${detail.conditions?.boating?.index}/10`}
                  sub={detail.conditions?.boating?.rating} color="text-blue-400" isDarkMode={isDarkMode} />
                <StatCard icon={Activity} label="Fishing" value={`${detail.conditions?.fishing?.index}/10`}
                  sub={detail.conditions?.fishing?.rating} color="text-emerald-400" isDarkMode={isDarkMode} />
                <StatCard icon={Droplets} label="River Wading" value={detail.river_conditions?.wading_status || "Unknown"}
                  sub={detail.river_conditions?.flow_status} color="text-blue-400" isDarkMode={isDarkMode} />
                <StatCard icon={Ship} label="Paddling" value={`${detail.river_conditions?.paddling_index || '—'}/10`}
                  sub={detail.river_conditions?.paddling_rating} color="text-indigo-400" isDarkMode={isDarkMode} />
              </div>
            </section>

            {detail.river_conditions && (
              <section className="mb-8" data-testid="river-conditions-detail-section">
                <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>River Conditions</h2>
                <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div data-testid="river-flow-status-detail">
                      <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Flow Status</p>
                      <p className="text-lg font-bold">{detail.river_conditions.flow_status}</p>
                    </div>
                    <div data-testid="river-wading-status-detail">
                      <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Wading</p>
                      <p className="text-lg font-bold">{detail.river_conditions.wading_status}</p>
                    </div>
                    <div data-testid="river-paddling-status-detail">
                      <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Paddling</p>
                      <p className="text-lg font-bold">{detail.river_conditions.paddling_rating}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {detail.river_conditions.quality_notes?.map((note) => (
                      <span key={note} data-testid={`river-quality-note-${note.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`text-xs px-2 py-1 rounded-lg ${isDarkMode ? 'bg-cyan-500/10 text-cyan-200' : 'bg-cyan-50 text-cyan-800'}`}>{note}</span>
                    ))}
                  </div>
                </div>
              </section>
            )}

            <section className="mb-8">
              <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>7-Day Trends</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {detail.history?.water_temp?.length > 1 && (
                  <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                    <MiniChart data={detail.history.water_temp} dataKey="value_f" color="#22d3ee" label="Water Temperature" unit="°F" height={140} />
                  </div>
                )}
                {detail.history?.gage_height?.length > 1 && (
                  <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                    <MiniChart data={detail.history.gage_height} dataKey="value_ft" color="#3b82f6" label="Water Level (Gage Height)" unit=" ft" height={140} />
                  </div>
                )}
                {detail.history?.discharge?.length > 1 && (
                  <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                    <MiniChart data={detail.history.discharge} dataKey="value_cfs" color="#6366f1" label="Stream Discharge" unit=" cfs" height={140} />
                  </div>
                )}
              </div>
            </section>

            <div className={`text-center text-xs ${isDarkMode ? 'text-white/30' : 'text-gray-400'}`}>
              <p>Data source: {detail.source}</p>
              <a href={detail.usgs_url} data-testid="lake-usgs-source-link" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-1">
                View on USGS <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </>
        )}

        {!detail && !detailLoading && !loading && lakes.length === 0 && stations.length === 0 && (
          <div className="text-center py-20">
            <Waves className="w-16 h-16 mx-auto mb-4 text-blue-400 opacity-30" />
            <h2 className="text-xl font-bold mb-2">No Water Bodies Found</h2>
            <p className={`${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>No lakes or monitoring stations found near your location.</p>
          </div>
        )}

        {!selectedStation && !detailLoading && lakes.length > 0 && (
          <div className="text-center py-16">
            <Waves className="w-12 h-12 mx-auto mb-3 text-blue-400 opacity-30" />
            <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>
              Select a lake from the dropdown to view water conditions and USGS monitoring data.
            </p>
          </div>
        )}
      </div>
      
      {/* Click outside to close picker */}
      {showPicker && <div className="fixed inset-0 z-40" onClick={() => { setShowPicker(false); setSearchFilter(""); }} />}
    </div>
  );
}
